import {useState,useRef,useCallback,useEffect} from 'react'
import {useSelector,useDispatch} from 'react-redux'
import Webcam from "react-webcam"
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import styles from './MemoRecorder.module.css'
import {toggleShow} from './slice'

const Timer = ({ seconds, onEnd }) => {
  // initialize timeLeft with the seconds prop
  const [timeLeft, setTimeLeft] = useState(seconds);

  useEffect(() => {
    // exit early when we reach 0
    if (!timeLeft){ 
    	onEnd()
    	return
    };

    // save intervalId to clear the interval when the
    // component re-renders
    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    // clear interval on re-render to avoid memory leaks
    return () => clearInterval(intervalId);
    // add timeLeft as a dependency to re-rerun the effect
    // when we update it
  }, [timeLeft]);
  return (
    <div className="text-center text-danger">time left: {timeLeft}</div>
  );
};


const MemoRecorder = ({handleFinish})=>{
	const dispatch = useDispatch()
	const show = useSelector(state=>state.memoRecorder.show)
	const [memoType,setMemoType] = useState('video')
	
	const handleToggleAudioOnly = e => {
		setMemoType(e.target.value)
	}

	const webcamRef = useRef(null)
	const mediaRecorderRef = useRef(null)
	const [capturing, setCapturing] = useState(false)
	const [recordedChunks, setRecordedChunks] = useState([])
	const [previewData,setPreviewData] = useState(null)

	const handleStartCaptureClick = useCallback(() => {
		setRecordedChunks([])
		setCapturing(true);
		mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream);
		mediaRecorderRef.current.addEventListener(
			"dataavailable",
			handleDataAvailable
		);
		//pull data ever 500ms
		mediaRecorderRef.current.start(500);
	}, [webcamRef, setCapturing, mediaRecorderRef])

	const handleDataAvailable = useCallback(
		({ data }) => {
			if (data.size > 0) {
				setRecordedChunks((prev) => prev.concat(data));
			}
		},
		[setRecordedChunks]
	)

	const handleStopCaptureClick = useCallback(async () => {
		setCapturing(false)
		mediaRecorderRef.current.stop()
		const mimeType = recordedChunks[0].type.split(';')[0]
		const mediaType = mimeType.split('/')[0]
		const extension = mimeType.split('/')[1]
		const blob = await new Blob(recordedChunks, { type : mimeType })
		setPreviewData(blob)
		setRecordedChunks([])
	}, [mediaRecorderRef, webcamRef, setCapturing,recordedChunks])

	const handleClose = useCallback(async () => {
		setRecordedChunks([])
		setPreviewData(null)
		dispatch(toggleShow())
	}, [setRecordedChunks])

	const handleFinishLocal = async () => {
		if(Boolean(previewData)){
			handleFinish({memo:previewData,type:memoType})
			setPreviewData(null)
			dispatch(toggleShow())
		}
	}

	const handleDiscard = useCallback(async () => {
		setPreviewData(null)
	}, [previewData])

	if(!show) return null

	return (
		<div onClick={handleClose} className={styles.Recorder}>
			<div onClick={e=>{e.stopPropagation()}}>
				<div className="d-flex align-items-center justify-content-between">
					Memo Recorder
					<Button 
						variant="outline-danger"
						onClick={handleClose}
						size="sm"
					>
						<i className="fa fa-times"/>
					</Button>
				</div>
				<hr className="my-1"/>
				{(!capturing&&Boolean(previewData))?
					<>
						<video 
							src={URL.createObjectURL(previewData)}
							className="d-block w-100 rounded" 
							controls 
						/>
						<div className="d-flex mt-2">
							<Button 
								onClick={handleDiscard}
								style={{flex:'0 0 auto'}} 
								variant="outline-danger" 
								size="sm"
							>
								<i className="fa fa-trash"/> Discard
							</Button>
							<OverlayTrigger overlay={
								<Tooltip>Use this memo and optionally add a message</Tooltip>
							}>
								<Button 
									onClick={handleFinishLocal}
									style={{flex:'0 0 auto'}} 
									variant="outline-success" 
									className="ml-1"
									size="sm"
								>
									Use memo <i class="fas fa-arrow-right"></i>
								</Button>
							</OverlayTrigger>
						</div>
					</>
					:
					<>
						<Webcam 
							style={{display:memoType === 'video' ? 'block' : 'none'}}
							videoConstraints={memoType === 'video'}
							className="w-100 rounded" 
							ref={webcamRef}
						/>
						{memoType === 'voice'&&
							<div className="px-5 py-3 text-center">
								<i className="fas fa-microphone"/> Voice Memo
							</div>
						}
						{capturing&&
							<Timer onEnd={handleStopCaptureClick} seconds={60}/>
						}
						<div className="d-flex mt-2">
							<Form.Control 
								onChange={handleToggleAudioOnly}
								value={memoType}
								as="select"
								size="sm"
							>
								<option value="video">Video Memo</option>
								<option value="voice">Voice Memo</option>
							</Form.Control>
							<Button 
								onClick={
									capturing ?
									handleStopCaptureClick : 
									handleStartCaptureClick
								}
								style={{flex:'0 0 auto'}} 
								variant="outline-danger" 
								className="ml-1"
								size="sm"
							>
								<i className={capturing?"fa fa-stop-circle":"fa fa-circle"}/> {capturing?'Stop':'Record'}
							</Button>
						</div>
					</>
				}
				<div className="text-center">
					<small>
						<i className="fa fa-info-circle mr-1"/>
						{
							(!capturing&&!previewData)?
							'Before sending you\'ll be able to review your recording':
							'Note: memos expire 1 week after they are sent.'
						}
					</small>
				</div>
			</div>
		</div>
	)
}

export default MemoRecorder